import "./site/error-beacon";
import { Frequency, FrequencyGhost, FrequencySample } from "./games/frequency/frequency";
import { recordRun, showXpToast } from "./site/profile";
import { getDailySeed, markDailyComplete, isDailyRun } from "./site/daily";
import "./ui/styles.css";

const PB_KEY = "aether.frequency_pb";
const HANDLE_KEY = "aether.handle";
// Session-only ghost: the best run since this tab opened. Cleared on
// tab close — that's the design: "best on this seed" is meaningless
// because seeds are fresh per run, so the ghost is "best you've done
// in this session" regardless of seed.
const GHOST_KEY = "aether.frequency.ghost";

interface StoredGhost { score: number; samples: FrequencySample[] }

function loadGhost(): FrequencyGhost | null {
  try {
    const raw = sessionStorage.getItem(GHOST_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw) as StoredGhost;
    if (!obj || !Array.isArray(obj.samples) || obj.samples.length < 2) return null;
    return { samples: obj.samples };
  } catch { return null; }
}

function maybeSaveGhost(samples: FrequencySample[], score: number) {
  try {
    const raw = sessionStorage.getItem(GHOST_KEY);
    const prior = raw ? (JSON.parse(raw) as StoredGhost) : null;
    if (prior && prior.score >= score) return;
    const payload: StoredGhost = { score, samples };
    sessionStorage.setItem(GHOST_KEY, JSON.stringify(payload));
  } catch { /* sessionStorage disabled — fine */ }
}

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startFrequencyRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/frequency", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

async function finishFrequencyRun(
  runId: string,
  handle: string,
  samples: FrequencySample[],
  hardMode: boolean,
  timeToFirstSampleMs: number | null,
) {
  try {
    const res = await fetch(`/api/runs/frequency/${runId}`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({
        handle, samples, hard_mode: hardMode,
        time_to_first_sample_ms: timeToFirstSampleMs,
      }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

/** v5(b): characterize tuner cadence on the game-over card. Buckets
 *  the per-pair |delta value| signal (avg_value_step_per_sample, §3
 *  Q6) into three categories. Per docs/dossier-games/11-frequency/
 *  07-telemetry.md §5 privacy posture, cadence-derived tags must be
 *  framed categorically — numeric stats like sample_dt_stdev_ms are
 *  bot-detect signals and must not leak client-side. */
function cadenceTag(avgValueStep: number): string {
  if (avgValueStep <= 0) return "";
  if (avgValueStep < 0.03) return "steady tuning";
  if (avgValueStep < 0.10) return "drifting tuning";
  return "erratic tuning";
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudInband = document.getElementById("hud-inband")!;
const hudTime = document.getElementById("hud-time")!;
const hudScore = document.getElementById("hud-score")!;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;
const hardModeCheckbox = document.getElementById("hard-mode-checkbox") as HTMLInputElement | null;

let game: Frequency | null = null;
let activeRunId: string | null = null;
let pendingSamples: FrequencySample[] | null = null;
let pendingHardMode = false;
let pendingTimeToFirstSampleMs: number | null = null;

async function startRun() {
  if (game) game.dispose();
  // v5(b): time-to-first-sample is ms-since-page-load at Begin click.
  // The first sample fires automatically ~50ms after game.start(), so
  // performance.now() here ≈ time-to-first-sample. Per dossier §3 Q7
  // — the onboarding signal that says how long the rules card held
  // the player's attention before they pressed Begin.
  const timeToFirstSampleMs = Math.round(performance.now());
  const dseed = getDailySeed();
  const runStart = dseed === null ? await startFrequencyRun() : null;
  activeRunId = runStart?.run_id ?? null;
  const seed = dseed ?? runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);
  // Per-run flag: read at run-start, frozen for this run so the player
  // can't toggle mid-run. Default-off per spec.
  const hardMode = hardModeCheckbox?.checked === true;

  game = new Frequency(canvas, {
    onHud: (runMs, inside, scoreEstimate) => {
      hudInband.textContent = String(inside);
      hudTime.textContent = ((60_000 - runMs) / 1000).toFixed(1);
      hudScore.textContent = scoreEstimate.toLocaleString();
    },
    onGameOver: async ({ samples, scoreEstimate, bestStreak, insideCount }) => {
      hud.dataset.state = "over";
      msgTitle.textContent = "Run complete";
      msgBody.textContent = `${scoreEstimate.toLocaleString()} pts · ${insideCount} in-band · streak ${bestStreak}`;
      btnStart.textContent = "Try again";
      maybeSaveGhost(samples, scoreEstimate);
      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (scoreEstimate > pb) localStorage.setItem(PB_KEY, String(scoreEstimate));
      } catch { /* private */ }
      const xp = recordRun({ game: "frequency", score: scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (isDailyRun()) markDailyComplete("frequency", scoreEstimate);

      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishFrequencyRun(activeRunId, handle, samples, hardMode, timeToFirstSampleMs);
        if (res) {
          msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
          if (typeof res.avg_value_step_per_sample === "number") {
            const tag = cadenceTag(res.avg_value_step_per_sample);
            if (tag) msgBody.textContent += ` · ${tag}`;
          }
        }
      } else {
        pendingSamples = samples;
        pendingHardMode = hardMode;
        pendingTimeToFirstSampleMs = timeToFirstSampleMs;
        submitForm.hidden = false;
      }
    },
  }, seed, { ghost: loadGhost(), hardMode });
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Frequency";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingSamples) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishFrequencyRun(activeRunId, value, pendingSamples, pendingHardMode, pendingTimeToFirstSampleMs);
  pendingSamples = null;
  submitForm.hidden = true;
  if (res) {
    msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
    if (typeof res.avg_value_step_per_sample === "number") {
      const tag = cadenceTag(res.avg_value_step_per_sample);
      if (tag) msgBody.textContent += ` · ${tag}`;
    }
  }
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});
